import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Store, ArrowLeft, ArrowRight, Loader2, Check, CreditCard } from "lucide-react";
import api, { money } from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DELIVERY_FEE = 2.99;
const STEPS = ["Details", "Fulfilment", "Address", "Summary", "Payment"];

export default function Checkout() {
  const { items, subtotal, sellerCount, clear } = useBasket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "", customer_phone: user?.phone || "",
    fulfilment: "delivery", address_line1: "", address_line2: "",
    city: "", postcode: user?.postcode || "", note: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (items.length === 0) { navigate("/basket"); return null; }
  const delivery = form.fulfilment === "delivery" ? DELIVERY_FEE * sellerCount : 0;
  const total = subtotal + delivery;

  const next = () => {
    if (step === 0 && !form.customer_name) return toast.error("Please enter your name");
    if (step === 2 && form.fulfilment === "delivery" && (!form.address_line1 || !form.postcode))
      return toast.error("Please enter your delivery address");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        ...form,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        group_id: order.group_id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 min-h-[70vh]">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mb-5">Checkout</h1>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= step ? "bg-[#1A3C2A] text-white" : "bg-white border border-[#E5DEC9] text-[#9A9282]"}`}>
                {i < step ? <Check size={15} /> : i + 1}
              </div>
              <span className="text-[10px] mt-1 font-semibold text-[#5A626A] hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-[#1A3C2A]" : "bg-[#E5DEC9]"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 sm:p-6">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-display font-bold text-[#1A3C2A]">Your details</h2>
            <div><Label className="text-xs">Full name</Label><Input data-testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs">Phone number</Label><Input data-testid="checkout-phone" value={form.customer_phone} onChange={set("customer_phone")} className="rounded-xl mt-1" placeholder="07700 900000" /></div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-display font-bold text-[#1A3C2A]">How would you like to receive your order?</h2>
            <div className="grid grid-cols-2 gap-3">
              {[["delivery", Truck, "Local delivery", `${money(DELIVERY_FEE)} per seller`], ["collection", Store, "Collect in person", "Free"]].map(([val, Icon, label, sub]) => (
                <button key={val} data-testid={`fulfilment-${val}`} onClick={() => setForm({ ...form, fulfilment: val })}
                  className={`p-4 rounded-2xl border-2 text-left transition-colors ${form.fulfilment === val ? "border-[#1A3C2A] bg-[#1A3C2A]/5" : "border-[#E5DEC9]"}`}>
                  <Icon size={22} className="text-[#1A3C2A]" />
                  <p className="font-display font-bold text-[#22252A] mt-2">{label}</p>
                  <p className="text-xs text-[#5A626A]">{sub}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-display font-bold text-[#1A3C2A]">{form.fulfilment === "delivery" ? "Delivery address" : "Collection details"}</h2>
            {form.fulfilment === "delivery" ? (
              <>
                <div><Label className="text-xs">Address line 1</Label><Input data-testid="checkout-address1" value={form.address_line1} onChange={set("address_line1")} className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs">Address line 2 (optional)</Label><Input data-testid="checkout-address2" value={form.address_line2} onChange={set("address_line2")} className="rounded-xl mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Town/City</Label><Input data-testid="checkout-city" value={form.city} onChange={set("city")} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs">Postcode</Label><Input data-testid="checkout-postcode" value={form.postcode} onChange={set("postcode")} className="rounded-xl mt-1" /></div>
                </div>
              </>
            ) : (
              <p className="text-sm text-[#5A626A]">The seller will contact you to arrange a collection time after your order is accepted.</p>
            )}
            <div><Label className="text-xs">Note for seller (optional)</Label><Textarea data-testid="checkout-note" value={form.note} onChange={set("note")} className="rounded-xl mt-1" /></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3">
            <h2 className="font-display font-bold text-[#1A3C2A]">Order summary</h2>
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span className="text-[#5A626A]">{it.quantity}× {it.name}</span>
                <span className="font-semibold">{money(it.price * it.quantity)}</span>
              </div>
            ))}
            <div className="h-px bg-[#E5DEC9] my-2" />
            <div className="flex justify-between text-sm"><span className="text-[#5A626A]">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#5A626A]">Delivery</span><span className="font-semibold">{money(delivery)}</span></div>
            <div className="flex justify-between text-base pt-1"><span className="font-bold text-[#1A3C2A]">Total</span><span data-testid="summary-total" className="font-display font-extrabold text-[#1A3C2A]">{money(total)}</span></div>
            <p className="text-xs text-[#5A626A] bg-[#FAF7F2] rounded-xl p-3 mt-2">{form.fulfilment === "delivery" ? `Delivering to ${form.address_line1}, ${form.postcode}` : "Collection in person"}</p>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-4 text-center py-4">
            <div className="w-14 h-14 rounded-full bg-[#1A3C2A]/8 flex items-center justify-center mx-auto"><CreditCard size={26} className="text-[#1A3C2A]" /></div>
            <h2 className="font-display font-bold text-[#1A3C2A]">Secure payment</h2>
            <p className="text-sm text-[#5A626A]">You'll be taken to our secure Stripe checkout to pay {money(total)} in GBP.</p>
            <p className="text-xs text-[#9A9282]">Test card: 4242 4242 4242 4242, any future date & CVC.</p>
            <Button data-testid="pay-now-button" onClick={pay} disabled={loading}
              className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06447] h-12 font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : `Pay ${money(total)}`}
            </Button>
          </div>
        )}

        {step < 4 && (
          <div className="flex gap-3 mt-6">
            {step > 0 && <Button data-testid="checkout-back" variant="outline" onClick={() => setStep((s) => s - 1)} className="rounded-full border-[#E5DEC9] gap-1"><ArrowLeft size={15} /> Back</Button>}
            <Button data-testid="checkout-next" onClick={next} className="flex-1 rounded-full bg-[#1A3C2A] hover:bg-[#132E20] gap-1">Continue <ArrowRight size={15} /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
